import { Card, Progress, Typography } from "antd";
import classNames from "classnames";
import { Skeleton } from "common/components/skeleton/skeleton";
import { Flex } from "common/components/flex/flex";
import { ModalPortal } from "common/components/modalPortal/modalPortal";
import { InfoIcon } from "common/icons/InfoIcon";
import { ArrowRightIcon } from "common/icons/ArrowRightIcon";
import { Tooltip } from "common/components/tooltip/tooltip";
import { MetricCandidatesModal } from "features/dashboard/sections/weeklyActivity/metricCard/metricCandidatesModal/metricCandidatesModal";
import * as styles from "features/dashboard/sections/weeklyActivity/metricCard/metricCard.module.scss";

const NAVY = "var(--navy-300)";
const GREEN = "var(--green-200)";

export const MetricCard = ({
  testId,
  label,
  icon,
  description,
  fetchCandidates,
  loading = false,
  total = 0,
  goal = null,
  showArrow = true,
}) => {
  const percentage = goal ? Math.round((total / goal) * 100) : null;
  const isClickable = total > 0;

  const getProgressColor = () => {
    if (!percentage) return NAVY;
    return percentage >= 100 ? GREEN : NAVY;
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Flex align="center" style={{ flexGrow: 1 }}>
          <Skeleton paragraph={{ rows: 3, width: ["60%", "80%", "100%"] }} />
        </Flex>
      );
    }

    return (
      <Flex direction="column" gap={12}>
        <Flex direction="column" gap={8}>
          <Flex justify="space-between" align="flex-start">
            {icon}
            <Tooltip title={description}>
              <InfoIcon
                className={`text-primary-accent ${styles.metricCard__infoIcon}`}
                data-testid={`metric-info-icon-${testId}`}
              />
            </Tooltip>
          </Flex>

          <Typography.Text className="font-weight-medium">
            {label}
          </Typography.Text>
        </Flex>

        <Flex direction="column" gap={6}>
          <div>
            {goal ? (
              <Flex justify="space-between" align="center">
                <div>
                  <span className="text-primary-accent font-weight-heavy">
                    {total}
                  </span>
                  <span className="text-secondary"> / {goal} goal</span>
                </div>
                <span
                  className={`text-primary-accent font-weight-heavy ${styles.metricCard__percentage}`}
                  style={{
                    color: percentage >= 100 ? GREEN : undefined,
                  }}
                >
                  {percentage}%
                </span>
              </Flex>
            ) : (
              <span className="text-primary-accent font-weight-heavy">
                {total}
              </span>
            )}
          </div>

          <Progress
            percent={Math.min(percentage ?? 0, 100)}
            showInfo={false}
            strokeColor={getProgressColor()}
            size="small"
            className={styles.metricCard__progress}
          />
        </Flex>
      </Flex>
    );
  };

  const renderMainContent = (props = {}) => (
    <Card
      className={classNames(styles.metricCard, {
        [styles.metricCard_clickable]: isClickable,
      })}
      {...props}
    >
      {renderContent()}

      {showArrow && (
        <span className={styles.metricCard__arrow}>
          <ArrowRightIcon className={styles.metricCard__arrowIcon} />
        </span>
      )}
    </Card>
  );

  if (isClickable) {
    return (
      <ModalPortal
        renderTrigger={(triggerProps) =>
          renderMainContent({
            role: "button",
            tabIndex: 0,
            ...triggerProps,
          })
        }
      >
        {({ open, closeModal }) => (
          <MetricCandidatesModal
            open={open}
            closeModal={closeModal}
            metric={label}
            fetchCandidates={fetchCandidates}
          />
        )}
      </ModalPortal>
    );
  }

  return renderMainContent();
};
